import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Fetcher, useFetchers, useNavigation } from "react-router-dom";

import Button, { ButtonProps } from "components/Button";
import { SpinnerTheme } from "components/Spinner";
import { SpinnerIcon } from "components/Spinner/SpinnerIcon";
import { Size, Sizes } from "styles/sizes";
import { cx } from "utils";


// TODO the options here must match the options for ButtonSizes
type IconSizes = Pick<Sizes<{ size: Size, extraButtonPadding: string }>, "SM" | "MD" | "LG">;
const iconSizes: IconSizes = {
  SM: { size: "XXS", extraButtonPadding: "!px-8" },
  MD: { size: "XS", extraButtonPadding: "!px-12" },
  LG: { size: "SM", extraButtonPadding: "!px-14" }
};

export type SubmitButtonProps = ButtonProps & {
  checkFormValidity?: boolean;
  trackDOMChanges?: boolean;
  showSubmitSpinnerIf?: () => boolean;
  fetchersToCheck?: "all" | Fetcher[]
  spinnerTheme?: SpinnerTheme;
};

const SubmitButton = forwardRef(
  (
    {
      checkFormValidity,
      trackDOMChanges = false,
      showSubmitSpinnerIf,
      fetchersToCheck = "all",
      spinnerTheme = "primary",
      className,
      ...props
    }: SubmitButtonProps,
    ref
  ) => {
    const navigation = useNavigation();
    const fetchers = useFetchers();

    const submitButtonRef = useRef<HTMLButtonElement>(null);
    useImperativeHandle(ref, () => submitButtonRef.current!, []);

    const [ isFormInvalid, setIsFormInvalid ] = useState(false);

    const { submittingFetcher, nonIdleFetcher } = useMemo(() => {
      const fetcherSources = fetchersToCheck === "all" ? fetchers : fetchersToCheck;
      return {
        submittingFetcher: fetcherSources.find(fetcher => fetcher.state === "submitting"),
        nonIdleFetcher: fetcherSources.find(fetcher => fetcher.state !== "idle")
      };
    }, [ fetchersToCheck, fetchers ]);

    const updateIsFormInvalid = useCallback(() => {
      if (submitButtonRef?.current?.form) {
        setIsFormInvalid(!submitButtonRef.current.form.checkValidity());
      }
    }, [ submitButtonRef ]);

    useEffect(() => {
      if (submitButtonRef.current && submitButtonRef.current.form) {
        if (checkFormValidity) {
          setIsFormInvalid(!submitButtonRef.current.form.checkValidity());
          submitButtonRef.current.form.addEventListener("input", (event) => {
            setIsFormInvalid(!(event.currentTarget as HTMLFormElement).checkValidity());
          });
        }

        if (trackDOMChanges) {
          const mutationObserver = new MutationObserver(updateIsFormInvalid);
          mutationObserver.observe(submitButtonRef.current.form, {
            childList: true,
            subtree: true,
            attributes: true
          });
        }
      }
    }, [ checkFormValidity, trackDOMChanges, updateIsFormInvalid ]);

    const isDisabled = props.disabled || isFormInvalid || navigation?.state === "submitting" || nonIdleFetcher !== undefined;

    const iconSize = iconSizes[props.size ?? "MD"]!;
    const hasText = props.text && (props.text.trim() !== "");
    const isIconOnly = !hasText && !!props.icon?.Icon;

    return <Button
      ref={ submitButtonRef }
      { ...props as ButtonProps }
      disabled={isDisabled}
      type="submit"
      className={ cx(className ?? "button button-primary", (!isIconOnly && (props.icon?.absolute !== false)) && iconSize.extraButtonPadding) }
      icon={ (((navigation?.state === "submitting") || (submittingFetcher !== undefined)) && (!showSubmitSpinnerIf || showSubmitSpinnerIf())) ?
        {
          ...props.icon,
          Icon: SpinnerIcon({ size: iconSize.size, theme: spinnerTheme }),
          absolute: props.icon?.absolute ?? true,
          // TODO bit of a mess with passing the size property here, the Button's Content component won't understand it as XS, SM etc that we pass here,
          //  but it's still needed because otherwise the Content component would apply a default size of "w-5 h-5"
          size: iconSize.size
        }
        : props.icon
      }
    />;
  });

export default SubmitButton;
